import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Search, ShieldCheck, ShieldOff, RotateCw, Eye } from "lucide-react";
import { VendorStatusBadge } from "@/pages/farm/shared";
import AdminFarmDrawer from "@/pages/admin/AdminFarmDrawer";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "pending_invite", label: "Pending invite" },
  { key: "profile_submitted", label: "Review pending" },
  { key: "approved", label: "Live" },
  { key: "suspended", label: "Suspended" },
];

export default function AdminFarmsTab() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", owner_name: "", farm_name: "", location: "", initial_password: "", story: "", image_url: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [drawerId, setDrawerId] = useState(null);

  const load = async () => {
    setError("");
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/admin/vendors", { params });
      setRows(data);
    } catch (e) { setError(e.response?.data?.detail || "Load error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const invite = async (e) => {
    e.preventDefault(); setBusy(true); setError(""); setSuccess("");
    try {
      const { data } = await api.post("/admin/farms/invite", form);
      setSuccess(`Invited ${data.email}. Share login: ${data.email} / ${data.initial_password}`);
      setForm({ email: "", owner_name: "", farm_name: "", location: "", initial_password: "", story: "", image_url: "" });
      setShowInvite(false);
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Invite failed"); } finally { setBusy(false); }
  };

  const approve = async (id) => { setBusy(true); try { await api.post(`/admin/vendors/${id}/approve`); await load(); } finally { setBusy(false); } };
  const suspend = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Suspend this farm? Their portal access will be blocked until reinstated.")) return;
    setBusy(true); try { await api.post(`/admin/vendors/${id}/suspend`); await load(); } finally { setBusy(false); }
  };
  const reinstate = async (id) => { setBusy(true); try { await api.post(`/admin/vendors/${id}/reinstate`); await load(); } finally { setBusy(false); } };

  return (
    <div className="space-y-6" data-testid="admin-farms-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-3xl">Farm partners</h2>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">Invite, approve, suspend, and audit every farm on the platform.</p>
        </div>
        <button onClick={() => setShowInvite(v => !v)} className="btn-primary" data-testid="invite-farm-toggle">
          <Plus size={16}/> {showInvite ? "Cancel" : "Invite a farm"}
        </button>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="farms-error">{error}</div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm text-emerald-800" data-testid="farms-success">{success}</div>}

      {showInvite && (
        <form onSubmit={invite} className="card-flat p-5 grid md:grid-cols-2 gap-3" data-testid="invite-form">
          <input required type="email" placeholder="owner@farm.com" className="input-base" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="invite-email"/>
          <input required placeholder="Owner full name" className="input-base" value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} data-testid="invite-owner"/>
          <input required placeholder="Farm name" className="input-base" value={form.farm_name} onChange={e => setForm({...form, farm_name: e.target.value})} data-testid="invite-farm"/>
          <input required placeholder="Location (City, ST)" className="input-base" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="invite-location"/>
          <input required minLength={6} placeholder="Initial password (6+ chars)" className="input-base md:col-span-2" value={form.initial_password} onChange={e => setForm({...form, initial_password: e.target.value})} data-testid="invite-pwd"/>
          <textarea placeholder="Optional farm story (the owner can finish this on first login)" rows={3} className="input-base md:col-span-2" value={form.story} onChange={e => setForm({...form, story: e.target.value})} data-testid="invite-story"/>
          <button disabled={busy} type="submit" className="btn-primary md:col-span-2" data-testid="invite-submit">{busy ? "Creating…" : "Create farm + share credentials"}</button>
        </form>
      )}

      <div className="flex gap-3 items-center flex-wrap" data-testid="farms-toolbar">
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map(f => (
            <button key={f.key} onClick={() => setStatus(f.key)} data-testid={`status-${f.key || 'all'}`}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium ${status === f.key ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <form onSubmit={e => { e.preventDefault(); load(); }} className="flex items-center gap-2 ml-auto" data-testid="farms-search">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, owner, location" className="input-base pl-9 text-sm w-64" data-testid="search-input"/>
          </div>
          <button className="btn-secondary text-sm" data-testid="search-btn">Search</button>
        </form>
      </div>

      <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="farms-list">
        {!rows && <p className="p-6 text-sm text-[var(--ff-text-muted)]">Loading…</p>}
        {rows && rows.length === 0 && <p className="p-6 text-sm text-[var(--ff-text-muted)]">No farms match the current filter.</p>}
        {rows && rows.map(v => (
          <div key={v.id} className="p-5 flex items-center gap-4" data-testid={`farm-row-${v.id}`}>
            {v.image_url ? (
              <img src={v.image_url} className="w-14 h-14 rounded-full object-cover" alt={v.farm_name} onError={e => { e.currentTarget.style.display = "none"; }}/>
            ) : <div className="w-14 h-14 rounded-full bg-stone-200"/>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-display text-lg" data-testid={`farm-name-${v.id}`}>{v.farm_name}</p>
                <VendorStatusBadge status={v.vendor_status}/>
              </div>
              <p className="text-xs text-[var(--ff-text-muted)] mt-1">{v.location} · {v.owner_name} · {v.owner_email || "—"}</p>
              <p className="text-xs text-[var(--ff-text-muted)] mt-1">{v.drops_count} drops · {v.products_count} products</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDrawerId(v.id)} className="btn-secondary text-sm" data-testid={`view-backend-${v.id}`}>
                <Eye size={14}/> View backend
              </button>
              {(v.vendor_status === "profile_submitted" || v.vendor_status === "pending_invite") && (
                <button disabled={busy} onClick={() => approve(v.id)} className="btn-primary text-sm" data-testid={`approve-${v.id}`}>
                  <ShieldCheck size={14}/> Approve
                </button>
              )}
              {v.vendor_status === "approved" && (
                <button disabled={busy} onClick={() => suspend(v.id)} className="btn-secondary text-sm text-rose-700" data-testid={`suspend-${v.id}`}>
                  <ShieldOff size={14}/> Suspend
                </button>
              )}
              {v.vendor_status === "suspended" && (
                <button disabled={busy} onClick={() => reinstate(v.id)} className="btn-primary text-sm" data-testid={`reinstate-${v.id}`}>
                  <RotateCw size={14}/> Reinstate
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <AdminFarmDrawer vendorId={drawerId} onClose={() => setDrawerId(null)} />
    </div>
  );
}
